import React from "react";
import styled from "styled-components";

import { colorFromTheme } from "../../utils/color";
import Button from "../buttons/Button";
import KeyboardAccessible from "../containers/KeyboardAccessible";

type SkipContentValue = Record<string, React.RefObject<HTMLElement>>;
export const SkipContentContext = React.createContext<{
  skipContentTargets: SkipContentValue;
  setSkipContentTargets: React.Dispatch<React.SetStateAction<SkipContentValue>>;
}>({
  skipContentTargets: {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSkipContentTargets: () => {},
});

const SkipButton = styled(Button).attrs({
  variant: "plain",
})`
  border: 1px dashed ${colorFromTheme("surface5")};
  margin: 1rem;
`;

export const SkipContentProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [skipContentTargets, setSkipContentTargets] =
    React.useState<SkipContentValue>({});

  return (
    <SkipContentContext.Provider
      value={{ skipContentTargets, setSkipContentTargets }}
    >
      {children}
    </SkipContentContext.Provider>
  );
};

const findFocusableDescendentAndFocus = (element: HTMLElement | null): void => {
  const focusable = element?.querySelector<HTMLElement>(
    'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])',
  );
  // console.log({ focusable });
  focusable?.focus();
};

export const SkipContentButton = () => {
  const { skipContentTargets } = React.useContext(SkipContentContext);

  return (
    <>
      {Object.entries(skipContentTargets).map(([label, ref]) => (
        <KeyboardAccessible key={label}>
          <SkipButton
            title=""
            onClick={() => findFocusableDescendentAndFocus(ref.current)}
          >
            {label}
          </SkipButton>
        </KeyboardAccessible>
      ))}
    </>
  );
};

export const useSkipContent = (
  label: string,
  ref: React.RefObject<HTMLElement>,
) => {
  const { setSkipContentTargets } = React.useContext(SkipContentContext);

  React.useEffect(() => {
    setSkipContentTargets((targets) => ({
      ...targets,
      [label]: ref,
    }));

    return () => {
      setSkipContentTargets((targets) => {
        const { [label]: _, ...newTargets } = targets;
        return newTargets;
      });
    };
  }, [setSkipContentTargets, label, ref]);

  return { label, ref };
};
